import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconChevronRight, IconClose, IconPlus, IconSearch } from '@/components/Icons';
import {
  FILTER_CONDITIONS,
  activeFilters,
  newFilterRow,
  type ReportFilterRow,
} from '@/features/reports/reportFilters';
import type { UseReportLayout } from '@/features/reports/useReportLayout';

/**
 * The toolbar every report page renders above its table: free-text search, a
 * Filters row-builder, and a Display-options column picker.
 *
 * Ported from sellokart-cloud's `components/ReportToolbar.tsx`. The date
 * range / branch / device picker is dropped here — this portal already has a
 * single global `FilterBar` (see `features/portal/FilterBar.tsx`) that every
 * report page shares, so there is no per-page period button to port.
 *
 * Search and Filters are lifted to the caller: on Orders (server-paginated)
 * they go into the API query; on every other page (which already fetches its
 * whole result set for the range) they run against the fetched rows via
 * `applyClientFilters`.
 */

// ── Popover shell ─────────────────────────────────────────────────────

/**
 * A button that toggles a panel anchored beneath it. Closes on Escape and
 * on a click outside, and marks the trigger with `aria-expanded` so the
 * state is announced rather than only visible.
 */
function Popover({
  label,
  icon,
  active = false,
  children,
}: {
  label: ReactNode;
  icon?: ReactNode;
  active?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className={`btn btn-sm ${
          active ? 'border-brand-400 bg-brand-50 text-brand-700' : 'btn-secondary'
        }`}
      >
        {icon}
        {label}
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute left-0 z-30 mt-2 rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

// ── Search ────────────────────────────────────────────────────────────

export function ReportSearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  const id = useId();
  return (
    <div className="relative w-full sm:w-72">
      <label className="sr-only" htmlFor={id}>
        {placeholder}
      </label>
      <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        id={id}
        type="search"
        className="input pl-9"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

// ── Filters ───────────────────────────────────────────────────────────

export interface ReportFiltersButtonProps {
  /** Filterable field names this page offers. */
  fields: readonly string[];
  /** The rows currently applied (the page owns these). */
  applied: readonly ReportFilterRow[];
  matchAny: boolean;
  /** Fired only on Apply / Clear — never per keystroke. */
  onApply: (rows: ReportFilterRow[], matchAny: boolean) => void;
  placeholderFor?: (field: string) => string;
}

/**
 * Edits a DRAFT copy of the rows; nothing reaches the page (and so no
 * refetch/refilter happens) until Apply is pressed.
 */
export function ReportFiltersButton({
  fields,
  applied,
  matchAny,
  onApply,
  placeholderFor,
}: ReportFiltersButtonProps) {
  const [draft, setDraft] = useState<ReportFilterRow[]>(() => applied.map((row) => ({ ...row })));
  const [draftMatchAny, setDraftMatchAny] = useState(matchAny);
  const appliedCount = activeFilters(applied).length;

  // Re-seed the draft whenever what's applied changes, so re-opening never
  // shows an abandoned edit.
  useEffect(() => {
    setDraft(applied.map((row) => ({ ...row })));
    setDraftMatchAny(matchAny);
  }, [applied, matchAny]);

  const update = (index: number, patch: Partial<ReportFilterRow>) => {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <Popover
      active={appliedCount > 0}
      label={appliedCount > 0 ? `Filters (${appliedCount})` : 'Filters'}
    >
      {(close) => (
        <div className="w-[36rem]">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Filters</h2>
              <button
                type="button"
                onClick={() => setDraftMatchAny((value) => !value)}
                className="rounded-full bg-brand-600/10 px-2.5 py-1 text-xs font-medium text-brand-700"
              >
                {draftMatchAny ? 'Match any (OR)' : 'Match all (AND)'}
              </button>
            </div>
            <div className="flex items-center gap-1">
              {draft.length > 0 ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-red-600 hover:underline"
                  onClick={() => {
                    setDraft([]);
                    onApply([], draftMatchAny);
                  }}
                >
                  Clear filter
                </button>
              ) : null}
              <button
                type="button"
                aria-label="Close filters"
                className="rounded p-1 text-red-600 hover:bg-red-50"
                onClick={close}
              >
                <IconClose className="h-4 w-4" />
              </button>
            </div>
          </div>

          {draft.map((row, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <select
                aria-label={`Filter ${index + 1} field`}
                className="input flex-1"
                value={row.field}
                onChange={(event) => update(index, { field: event.target.value })}
              >
                {fields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
              <select
                aria-label={`Filter ${index + 1} condition`}
                className="input flex-1"
                value={row.condition}
                onChange={(event) =>
                  update(index, { condition: event.target.value as ReportFilterRow['condition'] })
                }
              >
                {FILTER_CONDITIONS.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
              <input
                aria-label={`Filter ${index + 1} value`}
                className="input flex-1"
                placeholder={placeholderFor?.(row.field) ?? 'value'}
                value={row.value}
                onChange={(event) => update(index, { value: event.target.value })}
              />
              <button
                type="button"
                aria-label={`Remove filter ${index + 1}`}
                className="rounded p-1.5 text-red-600 hover:bg-red-50"
                onClick={() => setDraft((rows) => rows.filter((_, i) => i !== index))}
              >
                <IconClose className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-sm btn-ghost text-brand-700"
            onClick={() => setDraft((rows) => [...rows, newFilterRow(fields[0])])}
          >
            <IconPlus className="h-4 w-4" />
            Add filter
          </button>

          <div className="mt-3 flex justify-end border-t border-slate-200 pt-3">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                onApply(
                  draft.map((row) => ({ ...row })),
                  draftMatchAny,
                );
                close();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

// ── Display options ───────────────────────────────────────────────────

export interface ReportColumnsButtonProps {
  layout: UseReportLayout;
  /** Toggleable columns as key → header label. */
  labels: Readonly<Record<string, string>>;
}

/** Show/hide a column, and move it in the order. */
export function ReportColumnsButton({ layout, labels }: ReportColumnsButtonProps) {
  const keys = layout.orderedKeys.filter((key) => labels[key] !== undefined);

  return (
    <Popover label="Display options">
      {() => (
        <div className="w-80">
          <ul className="max-h-80 overflow-y-auto">
            {keys.map((key, index) => (
              <li key={key} className="flex items-center gap-2 py-1">
                <span className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`Move ${labels[key]} up`}
                    disabled={index === 0}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    onClick={() => layout.move(key, index - 1)}
                  >
                    <span className="block rotate-[-90deg]">
                      <IconChevronRight className="h-4 w-4" />
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${labels[key]} down`}
                    disabled={index === keys.length - 1}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    onClick={() => layout.move(key, index + 1)}
                  >
                    <span className="block rotate-90">
                      <IconChevronRight className="h-4 w-4" />
                    </span>
                  </button>
                </span>
                <label className="flex flex-1 items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={layout.isVisible(key)}
                    onChange={(event) => layout.setHidden(key, !event.target.checked)}
                  />
                  {labels[key]}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-between border-t border-slate-200 pt-2">
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:underline"
              onClick={() => layout.setAllHidden(keys, true)}
            >
              Hide all
            </button>
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:underline"
              onClick={() => layout.setAllHidden(keys, false)}
            >
              Show all
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

/** The row every report page renders: search · Filters · Display options. */
export function ReportToolbar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2">{children}</div>;
}
